import * as THREE from 'three'
import type Terrain from '../terrain'

/**
 * Voxel line-of-sight test: samples the segment every half block against
 * solid terrain. Used for mob aggro, creeper fuses, and skeleton bows.
 */
export function hasLineOfSight(
  terrain: Terrain,
  from: THREE.Vector3,
  to: THREE.Vector3,
  maxDistance = 40
) {
  const dx = to.x - from.x
  const dy = to.y - from.y
  const dz = to.z - from.z
  const distance = Math.sqrt(dx * dx + dy * dy + dz * dz)
  if (distance > maxDistance) return false
  if (distance < 0.001) return true

  const steps = Math.ceil(distance / 0.5)
  for (let i = 1; i < steps; i++) {
    const t = i / steps
    if (
      terrain.isSolidForMobAt(
        from.x + dx * t,
        from.y + dy * t,
        from.z + dz * t
      )
    ) {
      return false
    }
  }
  return true
}

/**
 * Minecraft Entity Textures are mapped in specific regions:
 * Head: front, back, left, right, top, bottom
 * Body: front, back, left, right, top, bottom
 * Leg: front, back, left, right, top, bottom
 *
 * This utility applies UV mapping for a BoxGeometry
 * given a texture coordinate box [x, y, w, h] from a 64x32 texture.
 */

// We assume a 64x32 texture.
const TEX_WIDTH = 64
const TEX_HEIGHT = 32



/**
 * Configure UVs of a BoxGeometry to match MC skin mappings
 * Box Geometry Faces order:
 * 1: Right
 * 2: Left
 * 3: Top
 * 4: Bottom
 * 5: Front
 * 6: Back
 * 
 * faceUVArray should be an array of exactly 6 [x,y,w,h] rects.
 */
export function setBoxUVs(geometry: THREE.BufferGeometry, faceUVArray: [number, number, number, number][]) {
  const uvAttribute = geometry.attributes.uv
  for (let face = 0; face < 6; face++) {
    const rect = faceUVArray[face]
    const minU = rect[0] / TEX_WIDTH
    const minV = 1.0 - (rect[1] + rect[3]) / TEX_HEIGHT
    const maxU = (rect[0] + rect[2]) / TEX_WIDTH
    const maxV = 1.0 - rect[1] / TEX_HEIGHT

    // 4 vertices per face (usually 2 triangles)
    // BoxGeometry vertex order for each face:
    // top-left, bottom-left, top-right, bottom-right (wait, depends on Three.js internals)
    // Actually, three.js BoxGeometry uses: 
    // [0,1, 1,1, 0,0, 1,0] in the newest versions.
    // We'll replace the uvs directly.
    const i = face * 4

    // Top-Left
    uvAttribute.setXY(i + 0, minU, maxV)
    // Top-Right
    uvAttribute.setXY(i + 1, maxU, maxV)
    // Bottom-Left
    uvAttribute.setXY(i + 2, minU, minV)
    // Bottom-Right
    uvAttribute.setXY(i + 3, maxU, minV)
  }
  uvAttribute.needsUpdate = true
}

export function buildMinecraftUVs(u: number, v: number, dx: number, dy: number, dz: number): [number, number, number, number][] {
  // A standard MC part mapping layout from [u,v]:
  // Right: [u, v+dz, dz, dy]
  // Front: [u+dz, v+dz, dx, dy]
  // Left: [u+dz+dx, v+dz, dz, dy]
  // Back: [u+dz+dx+dz, v+dz, dx, dy]
  // Top: [u+dz, v, dx, dz]
  // Bottom: [u+dz+dx, v, dx, dz]
  
  // Three.js Order: Right(0), Left(1), Top(2), Bottom(3), Front(4), Back(5)
  return [
    [u + dz + dx, v + dz, dz, dy],        // Right face in ThreeJS = Left face in MC skin
    [u, v + dz, dz, dy],                  // Left face in ThreeJS = Right face in MC
    [u + dz, v, dx, dz],                  // Top face
    [u + dz + dx, v, dx, dz],             // Bottom face
    [u + dz, v + dz, dx, dy],             // Front face
    [u + dz + dx + dz, v + dz, dx, dy]    // Back face
  ]
}
